import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthContext from "../../context/Auth/AuthContext";
import AuthService from "../../services/AuthService";
import styles from "./calculation.module.css";

const PayloadRRA = ({ onBack }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const handleBack = () => {
    if (typeof onBack === "function") onBack();
    else navigate("/home/calculation");
  };

  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [result, setResult] = useState(null);

  const moneyFromAPI = (s) =>
    Number(String(s || "0").replace(/\./g, "").replace(",", "."));
  const moneyBRL = (n) =>
    n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  // estado centralizado para manter a MESMA ESTRUTURA do payload
  const [form, setForm] = useState({
    numero_processo: "",
    honorarios: "",
    nome: "",

    valor_principal: "",
    valor_moratorio: "",
    valor_assistencia: "",
    valor_previdenciario: "",

    data_base: "",
    data_oficio: "",
    data_atualizacao: "",

    prioridade: {
      enabled: false,
      data: "",
      pagamento_principal: "",
      pagamento_moratorio: "",
      pagamento_assist_m: "",
      pagamento_ipesp_iamsp: "",
      pagamentos: [],
    },

    rra: {
      data_inicial: "",
      data_final: "",
      contagem_meses: "",
      decimo_terceiro: "NAO",
      desagio: "NAO",
      municipal_estadual: "",
      codigo_o_c: "",
    },
  });

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;

    setForm((f) => {
      const contagemStr =
        prefill?.rra?.contagem_meses != null
          ? String(prefill.rra.contagem_meses).replace(/\D/g, "")
          : "";

      const temContagem = contagemStr !== "";

      const coerceSimNao = (v) => {
        if (typeof v === "boolean") return v ? "SIM" : "NAO";
        const s = String(v || "").trim().toUpperCase();
        if (["SIM","S","TRUE","1","YES","Y"].includes(s)) return "SIM";
        if (["NAO","NÃO","N","FALSE","0","NO"].includes(s)) return "NAO";
        return "NAO"; // default quando não vier
      };

      return {
        ...f,
        ...(prefill.numero_processo ? { numero_processo: prefill.numero_processo } : {}),
        ...(prefill.nome ? { nome: prefill.nome } : {}),
        ...(prefill.data_base ? { data_base: prefill.data_base } : {}),
        ...(prefill.data_oficio ? { data_oficio: prefill.data_oficio } : {}),
        ...(prefill.data_atualizacao ? { data_atualizacao: prefill.data_atualizacao } : {}),

        ...(prefill.valor_principal ? { valor_principal: prefill.valor_principal } : {}),
        ...(prefill.valor_moratorio ? { valor_moratorio: prefill.valor_moratorio } : {}),
        ...(prefill.valor_assistencia ? { valor_assistencia: prefill.valor_assistencia } : {}),
        ...(prefill.valor_previdenciario ? { valor_previdenciario: prefill.valor_previdenciario } : {}),

        rra: {
          ...f.rra,
          ...(prefill.rra?.codigo_o_c ? { codigo_o_c: prefill.rra.codigo_o_c } : {}),

          // >>> novo: aplica 13º vindo do prefill (default "NAO")
          ...(prefill.rra
            ? { decimo_terceiro: coerceSimNao(prefill.rra.decimo_terceiro) }
            : {}),

          ...(temContagem
            ? { contagem_meses: contagemStr, data_inicial: "", data_final: "" }
            : {
                ...(prefill.rra?.data_inicial ? { data_inicial: prefill.rra.data_inicial } : {}),
                ...(prefill.rra?.data_final ? { data_final: prefill.rra.data_final } : {}),
              }),
        },
        prioridade: {
         ...f.prioridade,
         ...(Array.isArray(prefill?.prioridade?.pagamentos) &&
            prefill.prioridade.pagamentos.length > 0
            ? {
                pagamentos: prefill.prioridade.pagamentos.map(p => ({
                  data: p?.data || "",
                  pagamento_principal: p?.pagamento_principal || "",
                  pagamento_moratorio: p?.pagamento_moratorio || "",
                  pagamento_assist_m: p?.pagamento_assist_m || "",
                  pagamento_ipesp_iamsp: p?.pagamento_ipesp_iamsp || "",
                })),
              }
            : {}),
        },
      };
    });

    // opcional: limpar state de navegação pra não reaplicar ao voltar
    // navigate(".", { replace: true, state: null });
  }, [location.state]);

  const formatDateBR = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 8); // máx 8 dígitos
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);

    if (digits.length <= 2) return d;
    if (digits.length <= 4) return `${d}/${m}`;
    return `${d}/${m}/${y}`;
  };

  // Valida DD/MM/AAAA e checa calendário (inclui bissexto)
  const isValidDateBR = (value) => {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false;
    const [dd, mm, yyyy] = value.split("/").map(Number);
    if (yyyy < 1900 || yyyy > 2100) return false;
    if (mm < 1 || mm > 12) return false;

    const monthDays = [31, (yyyy % 4 === 0 && yyyy % 100 !== 0) || (yyyy % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return dd >= 1 && dd <= monthDays[mm - 1];
  };

  const formatCurrencyBR = (v) => {
    const digits = String(v || "").replace(/\D/g, "");
    if (digits.length === 0) return ""; // << quando não digitou nada, fica vazio
    const int = parseInt(digits, 10);
    const cents = int / 100;
    return cents
      .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
      .replace(/\u00A0/g, " ");
  };

  // Converte "R$ 1.234,56" -> 1234.56 (Number)
  const currencyToNumber = (v) => {
    if (v == null || v === "") return 0;
    const cleaned = String(v)
      .replace(/\s/g, "")
      .replace(/[^0-9,-]/g, "") // mantém dígitos, vírgula e sinal
      .replace(/\./g, "")       // tira separador de milhar
      .replace(",", ".");       // vírgula -> ponto
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const fixed2 = (n) => Number(n ?? 0).toFixed(2);

  // helpers de atualização (mantendo nested objs)
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setPrioridade = (key, value) =>
    setForm((f) => ({ ...f, prioridade: { ...f.prioridade, [key]: value } }));
  const setRra = (key, value) =>
    setForm((f) => ({ ...f, rra: { ...f.rra, [key]: value } }));

  const toNumber = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = Number(String(v).toString().replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

   const blankPagamento = () => ({
    data: "",
    pagamento_principal: "",
    pagamento_moratorio: "",
    pagamento_assist_m: "",
    pagamento_ipesp_iamsp: "",
  });

  const addPagamento = () =>
    setForm((f) => ({
      ...f,
      prioridade: {
        ...f.prioridade,
        pagamentos: [...(f.prioridade.pagamentos || []), blankPagamento()],
      },
    }));

  const removePagamento = (idx) =>
    setForm((f) => {
      const arr = [...(f.prioridade.pagamentos || [])];
      arr.splice(idx, 1);
      return { ...f, prioridade: { ...f.prioridade, pagamentos: arr } };
    });

  const setPagamentoField = (idx, key, value) =>
    setForm((f) => {
      const arr = [...(f.prioridade.pagamentos || [])];
      arr[idx] = { ...arr[idx], [key]: value };
      return { ...f, prioridade: { ...f.prioridade, pagamentos: arr } };
    });

  // true se pelo menos uma das datas (inicial/final) foi digitada
  const anyRraDateFilled = (ri, rf) => (ri && ri.length > 0) || (rf && rf.length > 0);

  // datas habilitadas quando contagem_meses vazio/0
  const areDatesEnabled = (contagem) => !(Number(contagem) > 0);

  // contagem habilitada quando não há datas
  const isContagemEnabled = (ri, rf) => !anyRraDateFilled(ri, rf);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (!user?.user?.id) {
     setErrorMessage("Usuário não autenticado. Faça login novamente.");
     return;
   }

   const hasDates = anyRraDateFilled(form.rra.data_inicial, form.rra.data_final);
  const contagem = Number(form.rra.contagem_meses || 0);

// Só datas OU só contagem
if (hasDates && contagem > 0) {
  setErrorMessage("Use apenas datas (inicial/final) ou apenas contagem de meses — não ambos.");
  return;
}

  // Se preencheu uma data, tem que preencher a outra e ambas válidas
  if (hasDates) {
    if (!form.rra.data_inicial || !form.rra.data_final) {
      setErrorMessage("Preencha data inicial e data final (RRA).");
      return;
    }
    if (!isValidDateBR(form.rra.data_inicial) || !isValidDateBR(form.rra.data_final)) {
      setErrorMessage("Datas do RRA inválidas. Use DD/MM/AAAA.");
      return;
    }
  }

  // contagem_meses negativa não pode
  if (contagem < 0) {
    setErrorMessage("Contagem de meses não pode ser negativa.");
    return;
  }
  if (!form.rra.municipal_estadual) {
    setErrorMessage("Selecione Municipal/Estadual (RRA).");
    return;
  }
  if (!String(form.rra.codigo_o_c || "").trim()) {
    setErrorMessage("Preencha o Código O/C (RRA).");
    return;
  }

  // --- PRIORIDADE: se SIM, todos obrigatórios ---
  if (form.prioridade.enabled) {
    const extras = form.prioridade.pagamentos || [];
    const temExtras = extras.length > 0;

    // Sempre valida o 1º bloco
    if (!form.prioridade.data || !isValidDateBR(form.prioridade.data)) {
      setErrorMessage("Preencha a data da prioridade (DD/MM/AAAA).");
      return;
    }
    const obrigatoriosPrimeiro = [
      form.prioridade.pagamento_principal,
      form.prioridade.pagamento_moratorio,
      form.prioridade.pagamento_assist_m,
      form.prioridade.pagamento_ipesp_iamsp,
    ];
    if (obrigatoriosPrimeiro.some(v => !String(v || "").trim())) {
      setErrorMessage("Preencha todos os pagamentos da prioridade.");
      return;
    }

    // Se tiver extras, valida cada um
    if (temExtras) {
      for (let i = 0; i < extras.length; i++) {
        const p = extras[i];
        if (!p.data || !isValidDateBR(p.data)) {
          setErrorMessage(`Data inválida no Pagamento ${i + 2} (DD/MM/AAAA).`);
          return;
        }
        const obrig = [
          p.pagamento_principal,
          p.pagamento_moratorio,
          p.pagamento_assist_m,
          p.pagamento_ipesp_iamsp,
        ];
        if (obrig.some(v => !String(v || "").trim())) {
          setErrorMessage(`Preencha todos os valores no Pagamento ${i + 2}.`);
          return;
        }
      }
    }
  }
    const prioridadeEnabled = !!form.prioridade.enabled;

    let prioridadePayload;
    if (prioridadeEnabled) {
      const first = {
        data: form.prioridade.data?.trim() ? form.prioridade.data.trim() : null,
        pagamento_principal: fixed2(currencyToNumber(form.prioridade.pagamento_principal)),
        pagamento_moratorio: fixed2(currencyToNumber(form.prioridade.pagamento_moratorio)),
        pagamento_assist_m: fixed2(currencyToNumber(form.prioridade.pagamento_assist_m)),
        pagamento_ipesp_iamsp: fixed2(currencyToNumber(form.prioridade.pagamento_ipesp_iamsp)),
      };
      const extras = (form.prioridade.pagamentos || []).map((p) => ({
        data: p.data?.trim() ? p.data.trim() : null,
        pagamento_principal: fixed2(currencyToNumber(p.pagamento_principal)),
        pagamento_moratorio: fixed2(currencyToNumber(p.pagamento_moratorio)),
        pagamento_assist_m: fixed2(currencyToNumber(p.pagamento_assist_m)),
        pagamento_ipesp_iamsp: fixed2(currencyToNumber(p.pagamento_ipesp_iamsp)),
      }));
      const all = [first, ...extras];

      // 1 pagamento => mantém o formato atual (sem array)
      // 2+ pagamentos => envia no formato com "pagamentos: []"
      if (all.length > 1) {
        prioridadePayload = { pagamentos: all };
      } else {
        prioridadePayload = first;
      }
    } else {
      prioridadePayload = {
        data: null,
        pagamento_principal: "0.00",
        pagamento_moratorio: "0.00",
        pagamento_assist_m: "0.00",
        pagamento_ipesp_iamsp: "0.00",
      };
    }
    // monta o payload FINAL mantendo chaves e tipos conforme especificação]

    const payload = {
      numero_processo: form.numero_processo,
      honorarios: form.honorarios,
      nome: form.nome,
      id_user: user.user.id,

      //valor_principal: currencyToNumber(form.valor_principal),
      valor_principal: fixed2(valorPrincipalLiquido),
      valor_moratorio: fixed2(currencyToNumber(form.valor_moratorio)),
      valor_assistencia: fixed2(currencyToNumber(form.valor_assistencia)),
      valor_previdenciario: fixed2(currencyToNumber(form.valor_previdenciario)),

      data_base: form.data_base,
      data_oficio: form.data_oficio,
      data_atualizacao: form.data_atualizacao,

      prioridade: {
        enabled: prioridadeEnabled,
        ...prioridadePayload,
      },

      rra: {
        // Se datas vazias → 0; se preenchidas → string "DD/MM/AAAA"
        data_inicial: form.rra.data_inicial ? form.rra.data_inicial : 0,
        data_final:   form.rra.data_final   ? form.rra.data_final   : 0,
        // contagem INT; vazio → 0
        contagem_meses: Number(form.rra.contagem_meses || 0),
        // dropdown SIM/NÃO → boolean
        decimo_terceiro: form.rra.decimo_terceiro === "SIM",
        desagio:         form.rra.desagio === "SIM",
        municipal_estadual: form.rra.municipal_estadual,
        codigo_o_c: form.rra.codigo_o_c,
      },
    };

    try {
      setSending(true);
      setErrorMessage("");

      const resp = await AuthService.enviarPayloadRRA(payload);
      const data = resp?.data ?? resp;

    // redireciona para a lista de cálculos
    navigate("/home/calculos", {
      replace: true,
      state: {
        flash: {
          type: "success",
          message: "Solicitação enviada com sucesso.",
          uuid: data?.uuid ?? null,
        },
      },
    });
    } catch (err) {
      console.error(err);
      setErrorMessage("Erro ao enviar a solicitação.");
    } finally {
      setSending(false);
    }

  };

  const vPrincipal = currencyToNumber(form.valor_principal);
  const vAssist    = currencyToNumber(form.valor_assistencia);
  const vPrev      = currencyToNumber(form.valor_previdenciario);
  const vMor       = currencyToNumber(form.valor_moratorio);

  const valorPrincipalLiquido = vPrincipal - vAssist - vPrev;
  const valorTotal            = valorPrincipalLiquido + vAssist + vPrev + vMor;

  // para mostrar vazio enquanto nada foi digitado
  const anyBaseFilled = Boolean(
    (form.valor_principal && String(form.valor_principal).trim()) ||
    (form.valor_assistencia && String(form.valor_assistencia).trim()) ||
    (form.valor_previdenciario && String(form.valor_previdenciario).trim()) ||
    (form.valor_moratorio && String(form.valor_moratorio).trim())
  );

  const principal = moneyFromAPI(result?.final_principal || 0);
  const moratorio = moneyFromAPI(result?.final_moratorio || 0);
  const ipesp    = moneyFromAPI(result?.final_ipesp_iamsp || 0);
  const honor    = moneyFromAPI(result?.honorario || 0);
  const total    = principal + moratorio + ipesp;

  return (
    <>
      <h1 style={{ color: "#fff", textAlign: "center", maxWidth: 600, margin: "0 auto 20px" }}>
        Cálculo Precatório
      </h1>

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit}>
          {/* Nº do processo + Nome */}
          <div className={styles.formGroup}>
            <div className={styles.halfWidth}>
              <label htmlFor="numero_processo"></label>
              <input
                id="numero_processo"
                type="text"
                value={form.numero_processo}
                onChange={(e) => setField("numero_processo", e.target.value)}
                placeholder="Número do processo"
                required
              />
            </div>
            <div className={styles.halfWidth}>
              <label htmlFor="nome"></label>
              <input
                id="nome"
                type="text"
                value={form.nome}
                onChange={(e) => setField("nome", e.target.value)}
                placeholder="Nome"
                required
              />
            </div>
          </div>

          {/* Valor principal + Valor moratório */}
          <div className={styles.formGroup}>
            <div className={styles.halfWidth}>
              <label htmlFor="valor_principal"></label>
              <input
                id="valor_principal"
                type="text"
                value={form.valor_principal}
                onChange={(e) => setField("valor_principal", formatCurrencyBR(e.target.value))}
                placeholder="Valor principal"
                required
              />
            </div>
            <div className={styles.halfWidth}>
              <label htmlFor="valor_moratorio"></label>
              <input
                id="valor_moratorio"
                type="text"
                value={form.valor_moratorio}
                onChange={(e) => setField("valor_moratorio", formatCurrencyBR(e.target.value))}
                placeholder="Valor moratório"
                required
              />
            </div>
          </div>

          {/* Valor assistência + Valor previdenciário */}
          <div className={styles.formGroup}>
            <div className={styles.halfWidth}>
              <label htmlFor="valor_assistencia"></label>
              <input
                id="valor_assistencia"
                type="text"
                value={form.valor_assistencia}
                onChange={(e) => setField("valor_assistencia", formatCurrencyBR(e.target.value))}
                placeholder="Valor assistência"
                required
              />
            </div>
            <div className={styles.halfWidth}>
              <label htmlFor="valor_previdenciario"></label>
              <input
                id="valor_previdenciario"
                type="text"
                value={form.valor_previdenciario}
                onChange={(e) => setField("valor_previdenciario", formatCurrencyBR(e.target.value))}
                placeholder="Valor previdenciário"
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.halfWidth}>
              <fieldset className={styles.fieldset}>
                <legend>Principal Líquido</legend>
                <div className={styles.totalValue}>
                  {anyBaseFilled ? moneyBRL(valorPrincipalLiquido) : "R$ 0,00"}
                </div>
              </fieldset>
            </div>

            <div className={styles.halfWidth}>
              <fieldset className={styles.fieldset}>
                <legend>Valor Total</legend>
                <div className={styles.totalValue}>
                  {anyBaseFilled ? moneyBRL(valorTotal) : "R$ 0,00"}
                </div>
              </fieldset>
            </div>
          </div>

          {/* Data base + Data do ofício */}
          <div className={styles.formGroup}>
            <div className={styles.halfWidth}>
              <label htmlFor="data_base"></label>
              <input
                id="data_base"
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={form.data_base}
                onChange={(e) => setField("data_base", formatDateBR(e.target.value))}
                onBlur={() => {
                  if (form.data_base && !isValidDateBR(form.data_base)) {
                    setErrorMessage("Data base inválida. Use DD/MM/AAAA.");
                  }
                }}
                placeholder="Data base (DD/MM/AAAA)"
                pattern="\d{2}/\d{2}/\d{4}"
                required
              />
            </div>
            <div className={styles.halfWidth}>
              <label htmlFor="data_oficio"></label>
              <input
                id="data_oficio"
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={form.data_oficio}
                onChange={(e) => setField("data_oficio", formatDateBR(e.target.value))}
                onBlur={() => {
                  if (form.data_oficio && !isValidDateBR(form.data_oficio)) {
                    setErrorMessage("Data do ofício inválida. Use DD/MM/AAAA.");
                  }
                }}
                placeholder="Data do ofício (DD/MM/AAAA)"
                pattern="\d{2}/\d{2}/\d{4}"
                required
              />
            </div>
          </div>

          {/* Data atualização + Honorários */}
          <div className={styles.formGroup}>
            <div className={styles.halfWidth}>
              <label htmlFor="data_atualizacao"></label>
              <input
                id="data_atualizacao"
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={form.data_atualizacao}
                onChange={(e) => setField("data_atualizacao", formatDateBR(e.target.value))}
                onBlur={() => {
                  if (form.data_atualizacao && !isValidDateBR(form.data_atualizacao)) {
                    setErrorMessage("Data de atualização inválida. Use DD/MM/AAAA.");
                  }
                }}
                placeholder="Data de atualização (DD/MM/AAAA)"
                pattern="\d{2}/\d{2}/\d{4}"
                required
              />
            </div>
            <div className={styles.halfWidth}>
              <label htmlFor="honorarios"></label>
              <input
                id="honorarios"
                type="text"
                value={form.honorarios}
                onChange={(e) => setField("honorarios", e.target.value)}
                placeholder="Honorários (%)"
                required
              />
            </div>
          </div>

          {/* RRA */}
          <fieldset className={styles.fieldset}>
            <legend>RRA</legend>

            {/* Datas do RRA (inicial/final) */}
            <div className={styles.formGroup}>
              <div className={styles.halfWidth}>
                <label htmlFor="rra_data_inicial"></label>
                <input
                  id="rra_data_inicial"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.rra.data_inicial}
                  onChange={(e) => {
                    const v = formatDateBR(e.target.value);
                    setRra("data_inicial", v);
                    if (v) setRra("contagem_meses", "");
                  }}
                  onBlur={() => {
                    if (form.rra.data_inicial && !isValidDateBR(form.rra.data_inicial)) {
                      setErrorMessage("Data inicial (RRA) inválida. Use DD/MM/AAAA.");
                    }
                  }}
                  placeholder="Data inicial (DD/MM/AAAA)"
                  disabled={!areDatesEnabled(form.rra.contagem_meses)}
                />
              </div>
              <div className={styles.halfWidth}>
                <label htmlFor="rra_data_final"></label>
                <input
                  id="rra_data_final"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.rra.data_final}
                  onChange={(e) => {
                    const v = formatDateBR(e.target.value);
                    setRra("data_final", v);
                    if (v) setRra("contagem_meses", "");
                  }}
                  onBlur={() => {
                    if (form.rra.data_final && !isValidDateBR(form.rra.data_final)) {
                      setErrorMessage("Data final (RRA) inválida. Use DD/MM/AAAA.");
                    }
                  }}
                  placeholder="Data final (DD/MM/AAAA)"
                  disabled={!areDatesEnabled(form.rra.contagem_meses)}
                />
              </div>
            </div>

            {/* Contagem de meses (sozinho) */}
            <div className={styles.formGroup}>
              <div className={styles.halfWidth}>
                <label htmlFor="contagem_meses"></label>
                <input
                  id="contagem_meses"
                  type="number"
                  min={0}
                  step={1}
                  value={form.rra.contagem_meses}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    setRra("contagem_meses", v);
                    if (Number(v) > 0) {
                      setRra("data_inicial", "");
                      setRra("data_final", "");
                    }
                  }}
                  placeholder="Contagem de meses"
                  disabled={!isContagemEnabled(form.rra.data_inicial, form.rra.data_final)}
                />
              </div>
            </div>

            {/* 13º + Deságio */}
            <div className={styles.formGroup}>
              <div className={styles.halfWidth}>
                <label htmlFor="decimo_terceiro"></label>
                <select
                  id="decimo_terceiro"
                  value={form.rra.decimo_terceiro}
                  onChange={(e) => setRra("decimo_terceiro", e.target.value)}
                  className={styles.select}
                  required
                >
                  <option value="NAO">13º: NÃO</option>
                  <option value="SIM">13º: SIM</option>
                </select>
              </div>
              <div className={styles.halfWidth}>
                <label htmlFor="desagio"></label>
                <select
                  id="desagio"
                  value={form.rra.desagio}
                  onChange={(e) => setRra("desagio", e.target.value)}
                  className={styles.select}
                  required
                >
                  <option value="NAO">Deságio: NÃO</option>
                  <option value="SIM">Deságio: SIM</option>
                </select>
              </div>
            </div>

            {/* Municipal/Estadual + Código O/C */}
            <div className={styles.formGroup}>
              <div className={styles.halfWidth}>
                <label htmlFor="municipal_estadual"></label>
                <select
                  id="municipal_estadual"
                  value={form.rra.municipal_estadual}
                  onChange={(e) => setRra("municipal_estadual", e.target.value)}
                  className={styles.select}
                  required
                >
                  <option value="">Selecione…</option>
                  <option value="E">Estadual</option>
                  <option value="M">Municipal</option>
                </select>
              </div>
              <div className={styles.halfWidth}>
                <label htmlFor="codigo_o_c"></label>
                <input
                  id="codigo_o_c"
                  type="text"
                  value={form.rra.codigo_o_c}
                  onChange={(e) => setRra("codigo_o_c", e.target.value)}
                  placeholder="Código O/C"
                  required
                />
              </div>
            </div>
          </fieldset>

          {/* Pergunta se existe prioridade */}
          <div className={styles.toggleGroup}>
            <span className={styles.toggleLabel}>Possui prioridade de pagamento?</span>
            <div className={styles.toggleChoices}>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="has_prioridade"
                  className={styles.radio}
                  checked={form.prioridade.enabled === true}
                  onChange={() => setPrioridade("enabled", true)}
                />
                <span className={styles.radioText}>SIM</span>
              </label>

              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="has_prioridade"
                  className={styles.radio}
                  checked={form.prioridade.enabled === false}
                  onChange={() => {
                    setPrioridade("enabled", false);
                    setPrioridade("data", "");
                    setPrioridade("pagamento_principal", 0);
                    setPrioridade("pagamento_moratorio", 0);
                    setPrioridade("pagamento_assist_m", 0);
                    setPrioridade("pagamento_ipesp_iamsp", 0);
                    setPrioridade("pagamentos", []);
                  }}
                />
                <span className={styles.radioText}>NÃO</span>
              </label>
            </div>
          </div>

          {/* PRIORIDADE (só aparece se marcado) */}
          {form.prioridade.enabled && (
            <fieldset className={styles.fieldset}>
              <legend>Prioridade</legend>

              <div className={styles.formGroup}>
                <div className={styles.halfWidth}>
                  <label htmlFor="prioridade_data"></label>
                  <input
                    id="prioridade_data"
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.prioridade.data}
                    onChange={(e) => setPrioridade("data", formatDateBR(e.target.value))}
                    onBlur={() => {
                      if (form.prioridade.data && !isValidDateBR(form.prioridade.data)) {
                        setErrorMessage("Data da prioridade inválida. Use DD/MM/AAAA.");
                      }
                    }}
                    placeholder="Data (DD/MM/AAAA)"
                    pattern="\d{2}/\d{2}/\d{4}"
                  />
                </div>
              </div>

              {/* Pagamento principal + moratório */}
              <div className={styles.formGroup}>
                <div className={styles.halfWidth}>
                  <label htmlFor="pagamento_principal"></label>
                  <input
                    id="pagamento_principal"
                    type="text"
                    value={form.prioridade.pagamento_principal}
                    onChange={(e) => setPrioridade("pagamento_principal", formatCurrencyBR(e.target.value))}
                    placeholder="Pagamento principal"
                  />
                </div>
                <div className={styles.halfWidth}>
                  <label htmlFor="pagamento_moratorio"></label>
                  <input
                    id="pagamento_moratorio"
                    type="text"
                    value={form.prioridade.pagamento_moratorio}
                    onChange={(e) => setPrioridade("pagamento_moratorio", formatCurrencyBR(e.target.value))}
                    placeholder="Pagamento moratório"
                  />
                </div>
              </div>

              {/* Pagamento assist. + IPESP/IAMSP */}
              <div className={styles.formGroup}>
                <div className={styles.halfWidth}>
                  <label htmlFor="pagamento_assist_m"></label>
                  <input
                    id="pagamento_assist_m"
                    type="text"
                    value={form.prioridade.pagamento_assist_m}
                    onChange={(e) => setPrioridade("pagamento_assist_m", formatCurrencyBR(e.target.value))}
                    placeholder="Pagamento assist. Medica"
                  />
                </div>
                <div className={styles.halfWidth}>
                  <label htmlFor="pagamento_ipesp_iamsp"></label>
                  <input
                    id="pagamento_ipesp_iamsp"
                    type="text"
                    value={form.prioridade.pagamento_ipesp_iamsp}
                    onChange={(e) => setPrioridade("pagamento_ipesp_iamsp", formatCurrencyBR(e.target.value))}
                    placeholder="Pagamento IPESP/IAMSP"
                  />
                </div>
              </div>
            </fieldset>
          )}
          {/* BLOCO(S) EXTRA(S) DE PAGAMENTO (Pagamento 2, 3, ...) */}
          {form.prioridade.enabled && (
            <>
              {form.prioridade.pagamentos?.map((p, idx) => (
                <fieldset className={styles.fieldset} key={idx}>
                  <legend>{`Prioridade — Pagamento ${idx + 2}`}</legend>

                  <div className={styles.formGroup}>
                    <div className={styles.halfWidth}>
                      <label htmlFor={`prioridade_data_${idx}`}></label>
                      <input
                        id={`prioridade_data_${idx}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        value={p.data}
                        onChange={(e) =>
                          setPagamentoField(idx, "data", formatDateBR(e.target.value))
                        }
                        onBlur={() => {
                          if (p.data && !isValidDateBR(p.data)) {
                            setErrorMessage("Data da prioridade inválida. Use DD/MM/AAAA.");
                          }
                        }}
                        placeholder="Data (DD/MM/AAAA)"
                        pattern="\d{2}/\d{2}/\d{4}"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <div className={styles.halfWidth}>
                      <label htmlFor={`pagamento_principal_${idx}`}></label>
                      <input
                        id={`pagamento_principal_${idx}`}
                        type="text"
                        value={p.pagamento_principal}
                        onChange={(e) =>
                          setPagamentoField(idx, "pagamento_principal", formatCurrencyBR(e.target.value))
                        }
                        placeholder="Pagamento principal"
                      />
                    </div>
                    <div className={styles.halfWidth}>
                      <label htmlFor={`pagamento_moratorio_${idx}`}></label>
                      <input
                        id={`pagamento_moratorio_${idx}`}
                        type="text"
                        value={p.pagamento_moratorio}
                        onChange={(e) =>
                          setPagamentoField(idx, "pagamento_moratorio", formatCurrencyBR(e.target.value))
                        }
                        placeholder="Pagamento moratório"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <div className={styles.halfWidth}>
                      <label htmlFor={`pagamento_assist_m_${idx}`}></label>
                      <input
                        id={`pagamento_assist_m_${idx}`}
                        type="text"
                        value={p.pagamento_assist_m}
                        onChange={(e) =>
                          setPagamentoField(idx, "pagamento_assist_m", formatCurrencyBR(e.target.value))
                        }
                        placeholder="Pagamento assist. Medica"
                      />
                    </div>
                    <div className={styles.halfWidth}>
                      <label htmlFor={`pagamento_ipesp_iamsp_${idx}`}></label>
                      <input
                        id={`pagamento_ipesp_iamsp_${idx}`}
                        type="text"
                        value={p.pagamento_ipesp_iamsp}
                        onChange={(e) =>
                          setPagamentoField(idx, "pagamento_ipesp_iamsp", formatCurrencyBR(e.target.value))
                        }
                        placeholder="Pagamento IPESP/IAMSP"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <button
                      type="button"
                      className={`${styles.btnLikeConsult} ${styles.btnInline}`}
                      onClick={() => removePagamento(idx)}
                    >
                      Remover este pagamento
                    </button>
                  </div>
                </fieldset>
              ))}

              <div className={styles.formGroup}>
                <button type="button" onClick={addPagamento} className={styles.btnInline}>
                  + Adicionar outro pagamento
                </button>
              </div>
            </>
          )}

          {/* Mensagens */}
          {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

          {/* Ações */}
          <div className={styles.formGroup}>
            <button type="button" className={`${styles.btnLikeConsult} ${styles.btnInline}`} onClick={handleBack}>
              Voltar
            </button>
            <button type="submit" disabled={sending}>
              {sending ? "Enviando..." : "Enviar"}
            </button>
          </div>
        </form>

        {successMessage && result && (
          <div className={styles.modal} role="dialog" aria-modal="true">
            <div className={styles.modalContent}>
              <h3 style={{ marginTop: 0 }}>Resultado do cálculo</h3>

              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 10px" }}>
                <li>Principal Atualizado: <strong>{moneyBRL(principal)}</strong></li>
                <li>Moratório Atualizado: <strong>{moneyBRL(moratorio)}</strong></li>
                <li>PESP + IAMSP Atualizado: <strong>{moneyBRL(ipesp)}</strong></li>

                {/* Só mostra se a API devolver esse campo */}
                {result?.final_rra && (
                  <li>RRA Atualizado: <strong>{moneyBRL(moneyFromAPI(result.final_rra))}</strong></li>
                )}

                <li>Honorário do Advogado: <strong>{moneyBRL(honor)}</strong></li>
              </ul>

              <hr />

              <p style={{ margin: "10px 0" }}>
                <strong>Total atualizado:</strong> {moneyBRL(total)}
              </p>

              {result?.uuid && (
                <p style={{ margin: "6px 0 12px", fontSize: 12, opacity: 0.8 }}>
                  Protocolo: {result.uuid}
                </p>
              )}

              <button
                type="button"
                onClick={() => { setSuccessMessage(false); setResult(null); }}
                className={styles.backButton}
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PayloadRRA;
